import { makeApi } from "@zodios/core";
import { z } from "zod";

import {
  ConversionActionsResponseSchema,
  ConversionMappingListResponseSchema,
  ConversionMappingResponseSchema,
  ConversionSettingResponseSchema,
  CreateConversionMappingRequestSchema,
  CreateConversionSettingRequestSchema,
  GoogleAdsAccountListResponseSchema,
  GoogleAdsManagerAccountSchema,
  LeadConversionReadinessResponseSchema,
  ManagerAccountRegistrationRequestSchema,
  ManagerAccountUpdateRequestSchema,
  UpdateConversionMappingRequestSchema,
  UpdateConversionSettingsRequestSchema,
  UpdateConversionSettingsResponseSchema,
} from "../models";

const api = makeApi([
  {
    alias: "registerManagerAccount",
    method: "post",
    path: "/api/v1/google-ads/manager-account",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: ManagerAccountRegistrationRequestSchema,
      },
    ],
    response: GoogleAdsManagerAccountSchema,
  },
  {
    alias: "getManagerAccount",
    method: "get",
    path: "/api/v1/google-ads/manager-account",
    parameters: [
      {
        type: "Query",
        name: "checkAccess",
        schema: z.boolean().optional(),
      },
    ],
    response: GoogleAdsManagerAccountSchema,
  },
  {
    alias: "updateManagerAccount",
    method: "put",
    path: "/api/v1/google-ads/manager-account",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: ManagerAccountUpdateRequestSchema,
      },
    ],
    response: GoogleAdsManagerAccountSchema,
  },
  {
    alias: "deleteManagerAccount",
    method: "delete",
    path: "/api/v1/google-ads/manager-account",
    parameters: [],
    response: z.union([z.void(), z.literal("")]),
  },
  {
    alias: "listManagerAccountAccounts",
    method: "get",
    path: "/api/v1/google-ads/manager-account/accounts",
    response: GoogleAdsAccountListResponseSchema,
  },
  {
    alias: "deleteAccount",
    method: "delete",
    path: "/api/v1/google-ads/manager-account/accounts/:googleAdsAccountId",
    parameters: [
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
    ],
    response: z.union([z.void(), z.literal("")]),
  },
  {
    alias: "reloadManagerAccountAccounts",
    method: "post",
    path: "/api/v1/google-ads/manager-account/accounts-reload",
    response: z.union([z.void(), z.literal("")]),
  },
  {
    alias: "createConversionSetting",
    method: "post",
    path: "/api/v1/google-ads/manager-account/accounts/:googleAdsAccountId/conversion-settings",
    parameters: [
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
      {
        type: "Body",
        name: "body",
        schema: CreateConversionSettingRequestSchema,
      },
    ],
    response: ConversionSettingResponseSchema,
  },
  {
    alias: "deleteConversionSetting",
    method: "delete",
    path: "/api/v1/google-ads/manager-account/accounts/:googleAdsAccountId/conversion-settings/:settingId",
    parameters: [
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
      {
        type: "Path",
        name: "settingId",
        schema: z.number().int(),
      },
    ],
    response: z.void(),
  },
  {
    alias: "updateConversionSettings",
    method: "put",
    path: "/api/v1/google-ads/manager-account/accounts/:googleAdsAccountId/conversion-settings",
    parameters: [
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
      {
        type: "Body",
        name: "body",
        schema: UpdateConversionSettingsRequestSchema,
      },
    ],
    response: UpdateConversionSettingsResponseSchema,
  },
  {
    alias: "listConversionMappings",
    method: "get",
    path: "/api/v1/google-ads/orgs/:orgUuid/manager_account/accounts/:googleAdsAccountId/conversion_mappings",
    parameters: [
      {
        type: "Path",
        name: "orgUuid",
        schema: z.string().uuid(),
      },
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
    ],
    response: ConversionMappingListResponseSchema,
  },
  {
    alias: "createConversionMapping",
    method: "post",
    path: "/api/v1/google-ads/orgs/:orgUuid/manager_account/accounts/:googleAdsAccountId/conversion_mappings",
    parameters: [
      {
        type: "Path",
        name: "orgUuid",
        schema: z.string().uuid(),
      },
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
      {
        type: "Body",
        name: "body",
        schema: CreateConversionMappingRequestSchema,
      },
    ],
    response: ConversionMappingResponseSchema,
  },
  {
    alias: "getConversionMapping",
    method: "get",
    path: "/api/v1/google-ads/orgs/:orgUuid/manager_account/accounts/:googleAdsAccountId/conversion_mappings/:conversionMappingId",
    parameters: [
      {
        type: "Path",
        name: "orgUuid",
        schema: z.string().uuid(),
      },
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
      {
        type: "Path",
        name: "conversionMappingId",
        schema: z.number().int(),
      },
    ],
    response: ConversionMappingResponseSchema,
  },
  {
    alias: "updateConversionMapping",
    method: "patch",
    path: "/api/v1/google-ads/orgs/:orgUuid/manager_account/accounts/:googleAdsAccountId/conversion_mappings/:conversionMappingId",
    parameters: [
      {
        type: "Path",
        name: "orgUuid",
        schema: z.string().uuid(),
      },
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
      {
        type: "Path",
        name: "conversionMappingId",
        schema: z.number().int(),
      },
      {
        type: "Body",
        name: "body",
        schema: UpdateConversionMappingRequestSchema,
      },
    ],
    response: ConversionMappingResponseSchema,
  },
  {
    alias: "deleteConversionMapping",
    method: "delete",
    path: "/api/v1/google-ads/orgs/:orgUuid/manager_account/accounts/:googleAdsAccountId/conversion_mappings/:conversionMappingId",
    parameters: [
      {
        type: "Path",
        name: "orgUuid",
        schema: z.string().uuid(),
      },
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
      {
        type: "Path",
        name: "conversionMappingId",
        schema: z.number().int(),
      },
    ],
    response: z.union([z.void(), z.literal("")]),
  },
  {
    alias: "getConversionActions",
    method: "get",
    path: "/api/v1/google-ads/orgs/:orgUuid/conversion-actions",
    parameters: [
      {
        type: "Path",
        name: "orgUuid",
        schema: z.string().uuid(),
      },
    ],
    response: ConversionActionsResponseSchema,
  },
  {
    alias: "getLeadConversionReadiness",
    method: "get",
    path: "/api/v1/google-ads/orgs/:orgUuid/manager_account/accounts/:googleAdsAccountId/lead_conversion_readiness",
    parameters: [
      {
        type: "Path",
        name: "orgUuid",
        schema: z.string().uuid(),
      },
      {
        type: "Path",
        name: "googleAdsAccountId",
        schema: z.number().int(),
      },
    ],
    response: LeadConversionReadinessResponseSchema,
  },
]);

export { api };
